import { useEffect } from 'react';

interface GoogleAnalyticsProps {
  measurementId: string;
}

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

const GoogleAnalytics: React.FC<GoogleAnalyticsProps> = ({ measurementId }) => {
  useEffect(() => {
    if (!measurementId || measurementId === 'YOUR_GA4_MEASUREMENT_ID') return;
    
    // SSR safety check
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    // Load Google Analytics script
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script1);

    // Initialize gtag with SSR safety
    const script2 = document.createElement('script');
    script2.innerHTML = `
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${measurementId}', {
          page_title: document.title,
          page_location: window.location.href
        });
      }
    `;
    document.head.appendChild(script2);

    // Cleanup function
    return () => {
      if (typeof document !== 'undefined') {
        document.head.removeChild(script1);
        document.head.removeChild(script2);
      }
    };
  }, [measurementId]);

  return null;
};

// Google Analytics tracking functions
export const trackEvent = (eventName: string, parameters: Record<string, any> = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, parameters);
  }
};

export const trackPurchase = (transactionId: string, value: number, currency: string = 'USD', items: any[] = []) => {
  trackEvent('purchase', {
    transaction_id: transactionId,
    value: value,
    currency: currency,
    items: items
  });
};

export const trackAddToCart = (currency: string, value: number, items: any[]) => {
  trackEvent('add_to_cart', {
    currency: currency,
    value: value,
    items: items
  });
};

export const trackViewItem = (currency: string, value: number, items: any[]) => {
  trackEvent('view_item', {
    currency: currency,
    value: value,
    items: items
  });
};

export const trackBeginCheckout = (currency: string, value: number, items: any[]) => {
  trackEvent('begin_checkout', {
    currency: currency,
    value: value,
    items: items
  });
};

export const trackSearch = (searchTerm: string) => {
  trackEvent('search', {
    search_term: searchTerm
  });
};

export default GoogleAnalytics;